import { useState, useEffect } from 'react';
import { binanceApi } from '../services/binance.api.service';

const emptyCurrencyBalances = () => ({
  bitcoin: { current: 0, total: 0 },
  ethereum: { current: 0, total: 0 },
  usd: { current: 0, total: 0 }
});

const normalizeAssetName = (value) => (value ?? '').toString().trim().toUpperCase();

const assetAliases = {
  BITCOIN: 'BTC',
  ETHEREUM: 'ETH',
  TETHER: 'USDT',
  'TETHER USDT': 'USDT',
  'US DOLLAR': 'USD',
  'US DOLLARS': 'USD',
  SOLANA: 'SOL',
  CARDANO: 'ADA',
  DOGECOIN: 'DOGE',
  LITECOIN: 'LTC',
  MONERO: 'XMR',
  POLYGON: 'MATIC',
  TRON: 'TRX'
};

const getWalletAssetName = (wallet) => normalizeAssetName(
  wallet?.asset_ticker
    ?? wallet?.asset_name
    ?? wallet?.asset_symbol
    ?? wallet?.symbol
    ?? wallet?.currency
    ?? wallet?.currency_name
    ?? wallet?.currency_symbol
    ?? wallet?.asset?.name
    ?? wallet?.asset?.ticker
    ?? wallet?.asset?.symbol
);

const parseBalance = (value) => {
  const parsed = Number.parseFloat((value ?? 0).toString().replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const useTotalBalance = (wallets) => {
  const [totalBalanceUSD, setTotalBalanceUSD] = useState(0);
  const [currentBalanceUSD, setCurrentBalanceUSD] = useState(0);
  const [currencyBalances, setCurrencyBalances] = useState(emptyCurrencyBalances);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calculateTotalBalance = async () => {
      if (!wallets || wallets.length === 0) {
        setTotalBalanceUSD(0);
        setCurrentBalanceUSD(0);
        setCurrencyBalances(emptyCurrencyBalances());
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const prices = await binanceApi.getCryptoPrices();

        const rates = {
          BTC: parseFloat(prices[0] || 0),
          ETH: parseFloat(prices[1] || 0),
          BNB: parseFloat(prices[2] || 0),
          SOL: parseFloat(prices[3] || 0),
          XRP: parseFloat(prices[4] || 0),
          ADA: parseFloat(prices[5] || 0),
          DOGE: parseFloat(prices[6] || 0),
          LTC: parseFloat(prices[7] || 0),
          MATIC: parseFloat(prices[8] || 0),
          TRX: parseFloat(prices[9] || 0),
          EUR: parseFloat(prices[10] || 0),
          USD: 1,
          USDT: 1
        };

        let totalUSD = 0;
        let currentUSD = 0;

        const balances = emptyCurrencyBalances();

        wallets.forEach((wallet) => {
          const currentBalance = parseBalance(wallet?.balance);
          const totalBalance = parseBalance(wallet?.total_balance ?? wallet?.balance);
          const assetName = getWalletAssetName(wallet);
          const assetTicker = assetAliases[assetName] ?? assetName;
          const rate = Number.isFinite(rates[assetTicker]) ? rates[assetTicker] : 0;
          const currentBalanceConverted = currentBalance * rate;
          const totalBalanceConverted = totalBalance * rate;

          switch (assetTicker) {
            case 'BTC':
              balances.bitcoin.current += currentBalanceConverted;
              balances.bitcoin.total += totalBalanceConverted;
              break;

            case 'ETH':
              balances.ethereum.current += currentBalanceConverted;
              balances.ethereum.total += totalBalanceConverted;
              break;

            case 'USD':
            case 'USDT':
              balances.usd.current += currentBalanceConverted;
              balances.usd.total += totalBalanceConverted;
              break;

            default:
              break;
          }

          currentUSD += currentBalanceConverted;
          totalUSD += totalBalanceConverted;
        });

        setCurrentBalanceUSD(currentUSD);
        setTotalBalanceUSD(totalUSD);
        setCurrencyBalances(balances);
      } catch (error) {
        console.error('useTotalBalance - Error calculating total balance:', error);
        setTotalBalanceUSD(0);
        setCurrentBalanceUSD(0);
        setCurrencyBalances(emptyCurrencyBalances());
      } finally {
        setLoading(false);
      }
    };

    calculateTotalBalance();
  }, [wallets]);

  return {
    totalBalanceUSD,
    currentBalanceUSD,
    currencyBalances,
    loading
  };
};
